/**
 * OTP Actions Hook
 *
 * Handles SMS/WhatsApp OTP verification and resend logic.
 * Includes countdown timer management.
 */

import { useCallback, useEffect } from 'react';
import {
  defaultOnboardingApiClient,
  type OnboardingApiClient,
} from '@/features/onboarding/api/onboarding-api-client';
import {
  SMS_RESEND_COUNTDOWN_SECONDS,
  SMS_MAX_RESEND_COUNT,
} from '@/features/onboarding/constants/onboarding.constants';
import type {
  OnboardingState,
  OnboardingFlowResponse,
} from '@/features/onboarding/types/onboarding.types';

interface UseOtpActionsProps {
  state: OnboardingState;
  setState: React.Dispatch<React.SetStateAction<OnboardingState>>;
  processResponse: (response: OnboardingFlowResponse) => Promise<void>;
  /** Injectable API client; defaults to the standard user-onboarding endpoints. */
  api?: OnboardingApiClient;
}

interface UseOtpActionsReturn {
  submitSmsCode: (code: string) => Promise<boolean>;
  resendSmsCode: () => Promise<boolean>;
  canResendSms: boolean;
}

/**
 * Hook for OTP-related actions (SMS/WhatsApp verification)
 */
export function useOtpActions({
  state,
  setState,
  processResponse,
  api = defaultOnboardingApiClient,
}: UseOtpActionsProps): UseOtpActionsReturn {
  // ============================================================================
  // SMS RESEND COUNTDOWN TIMER
  // ============================================================================

  useEffect(() => {
    if (state.smsResendCountdown > 0) {
      const timer = setTimeout(() => {
        setState(prev => ({
          ...prev,
          smsResendCountdown: prev.smsResendCountdown - 1,
        }));
      }, 1000);

      return () => clearTimeout(timer);
    }
  }, [state.smsResendCountdown, setState]);

  // ============================================================================
  // ACTIONS
  // ============================================================================

  /**
   * Submit SMS/OTP verification code
   */
  const submitSmsCode = useCallback(
    async (code: string) => {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      try {
        const response = await api.submitSmsCode(code);
        await processResponse(response);

        return true;
      } catch (error) {
        setState(prev => ({
          ...prev,
          error: error instanceof Error ? error.message : 'Verification failed',
          isLoading: false,
        }));

        return false;
      }
    },
    [api, setState, processResponse]
  );

  /**
   * Resend SMS/OTP code
   */
  const resendSmsCode = useCallback(async () => {
    if (
      state.smsResendCountdown > 0 ||
      state.smsResendCount >= SMS_MAX_RESEND_COUNT
    ) {
      return false;
    }

    setState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      await api.resendOtp();

      setState(prev => ({
        ...prev,
        smsResendCount: prev.smsResendCount + 1,
        smsResendCountdown: SMS_RESEND_COUNTDOWN_SECONDS,
        isLoading: false,
      }));

      return true;
    } catch (error) {
      setState(prev => ({
        ...prev,
        error: error instanceof Error ? error.message : 'Failed to resend',
        isLoading: false,
      }));

      return false;
    }
  }, [api, state.smsResendCountdown, state.smsResendCount, setState]);

  // ============================================================================
  // COMPUTED
  // ============================================================================

  const canResendSms =
    state.smsResendCountdown === 0 &&
    state.smsResendCount < SMS_MAX_RESEND_COUNT;

  return {
    submitSmsCode,
    resendSmsCode,
    canResendSms,
  };
}
